import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useState } from "react";
import { X, Minus, Plus, Check, Flame } from "lucide-react";
import { formatGNF, imgUrl } from "@/utils/format";

/**
 * Panneau de personnalisation d'un plat basé sur ses INGRÉDIENTS :
 *  - retirables : case cochée par défaut (décocher = « Sans X », gratuit)
 *  - duplicables : sélecteur Simple / Double (+prix) / Triple (+prix)…
 *  - cuisson : pills si le restaurant l'a activée
 * Props:
 *  - open, dish, initial ({ selected_options, quantity }), confirmLabel, onClose
 *  - onConfirm({ selected_options, quantity }) — selected_options = liste affichable
 *    [{kind, ingredient_id?, level_index?, value?, name, price_delta}]
 */
export default function DishOptionsModal({ open, dish, initial, confirmLabel, onClose, onConfirm }) {
  const { t } = useTranslation();
  const ingredients = useMemo(() => dish?.ingredients || [], [dish]);
  const cookingChoices = useMemo(() => dish?.cooking_choices || [], [dish]);
  const [removed, setRemoved] = useState([]);       // ingredient_ids décochés
  const [extras, setExtras] = useState({});         // ingredient_id -> level_index | null (null = simple)
  const [cooking, setCooking] = useState("");
  const [qty, setQty] = useState(1);

  useEffect(() => {
    if (!open || !dish) return;
    const prev = initial?.selected_options || [];
    setRemoved(prev.filter((o) => o.kind === "removed").map((o) => o.ingredient_id));
    const ex = {};
    prev.filter((o) => o.kind === "extra").forEach((o) => { ex[o.ingredient_id] = o.level_index; });
    setExtras(ex);
    const prevCooking = prev.find((o) => o.kind === "cooking")?.value;
    setCooking(prevCooking || dish.cooking_default || cookingChoices[0] || "");
    setQty(initial?.quantity || 1);
  }, [open, dish]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open || !dish) return null;

  const removables = ingredients.filter((i) => i.type === "removable");
  const duplicables = ingredients.filter((i) => i.type === "duplicable");
  const fixed = ingredients.filter((i) => i.type === "fixed");

  // Sélections résolues (liste affichable + prix)
  const resolved = [];
  removed.forEach((rid) => {
    const ing = removables.find((i) => i.ingredient_id === rid);
    if (ing) resolved.push({ kind: "removed", ingredient_id: rid, name: `Sans ${ing.name.toLowerCase()}`, price_delta: 0 });
  });
  Object.entries(extras).forEach(([iid, li]) => {
    if (li === null || li === undefined) return;
    const ing = duplicables.find((i) => i.ingredient_id === iid);
    const lv = ing?.levels?.[li];
    if (lv) resolved.push({ kind: "extra", ingredient_id: iid, level_index: li, name: `${lv.label} ${ing.name.toLowerCase()}`, price_delta: Number(lv.price_delta) || 0 });
  });
  if (cookingChoices.length && cooking) {
    resolved.push({ kind: "cooking", value: cooking, name: `Cuisson : ${cooking}`, price_delta: 0 });
  }
  const optionsTotal = resolved.reduce((s, o) => s + o.price_delta, 0);
  const total = ((dish.price || 0) + optionsTotal) * qty;

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4" data-testid="dish-options-modal">
      <div className="w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[92vh] flex flex-col overflow-hidden">
        <div className="relative">
          {dish.image && <img src={imgUrl(dish.image)} alt={dish.name} className="w-full h-36 sm:h-44 object-cover" />}
          <button onClick={onClose} data-testid="dish-options-close" className="absolute right-3 top-3 rounded-full bg-white/90 p-2 shadow hover:bg-white" aria-label={t("dopt.close")}>
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          <div>
            <h3 className="font-display text-xl font-bold">{dish.name}</h3>
            {dish.description && <p className="text-sm text-mute mt-1">{dish.description}</p>}
            <div className="text-sm font-semibold mt-1">{formatGNF(dish.price)}</div>
            {fixed.length > 0 && (
              <p className="text-[11px] text-mute mt-1" data-testid="fixed-ingredients">
                {t("dopt.base")} {fixed.map((i) => i.name.toLowerCase()).join(", ")}
              </p>
            )}
          </div>

          {/* Cuisson */}
          {cookingChoices.length > 0 && (
            <div data-testid="cooking-group">
              <h4 className="font-semibold text-sm mb-2 flex items-center gap-1.5"><Flame className="w-4 h-4 text-brand-red" /> {t("dopt.cooking")}</h4>
              <div className="flex flex-wrap gap-2">
                {cookingChoices.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setCooking(c)}
                    data-testid={`cooking-${c}`}
                    className={`rounded-full border px-3.5 py-1.5 text-sm transition-colors ${cooking === c ? "border-brand-red bg-[#FFF5F6] font-semibold" : "border-sand hover:border-mute"}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Suppléments payants */}
          {duplicables.length > 0 && (
            <div data-testid="duplicables-group">
              <h4 className="font-semibold text-sm mb-2">{t("dopt.extras")}</h4>
              <div className="space-y-3">
                {duplicables.map((ing) => {
                  const sel = extras[ing.ingredient_id];
                  return (
                    <div key={ing.ingredient_id}>
                      <div className="text-sm mb-1.5">{ing.name}</div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => setExtras((p) => ({ ...p, [ing.ingredient_id]: null }))}
                          data-testid={`extra-${ing.ingredient_id}-simple`}
                          className={`rounded-full border px-3.5 py-1.5 text-sm ${sel === null || sel === undefined ? "border-brand-red bg-[#FFF5F6] font-semibold" : "border-sand hover:border-mute"}`}
                        >
                          {t("dopt.single")}
                        </button>
                        {(ing.levels || []).map((lv, li) => (
                          <button
                            key={li}
                            type="button"
                            onClick={() => setExtras((p) => ({ ...p, [ing.ingredient_id]: li }))}
                            data-testid={`extra-${ing.ingredient_id}-${li}`}
                            className={`rounded-full border px-3.5 py-1.5 text-sm ${sel === li ? "border-brand-red bg-[#FFF5F6] font-semibold" : "border-sand hover:border-mute"}`}
                          >
                            {lv.label} {Number(lv.price_delta) > 0 && <span className="text-mute">+{formatGNF(lv.price_delta)}</span>}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Ingrédients retirables */}
          {removables.length > 0 && (
            <div data-testid="removables-group">
              <h4 className="font-semibold text-sm mb-2">{t("dopt.ingredients")} <span className="text-mute font-normal">{t("dopt.uncheckHint")}</span></h4>
              <div className="space-y-1.5">
                {removables.map((ing) => {
                  const present = !removed.includes(ing.ingredient_id);
                  return (
                    <button
                      key={ing.ingredient_id}
                      type="button"
                      onClick={() => setRemoved((p) => present ? [...p, ing.ingredient_id] : p.filter((x) => x !== ing.ingredient_id))}
                      data-testid={`ingredient-${ing.ingredient_id}`}
                      className={`w-full flex items-center gap-2 rounded-xl border px-3 py-2.5 text-sm transition-colors ${present ? "border-sand" : "border-brand-red bg-[#FFF5F6]"}`}
                    >
                      <span className={`w-4 h-4 shrink-0 grid place-items-center border rounded ${present ? "bg-brand-green border-brand-green text-white" : "border-mute"}`}>
                        {present && <Check className="w-3 h-3" />}
                      </span>
                      <span className={present ? "" : "line-through text-mute"}>{ing.name}</span>
                      {!present && <span className="ml-auto text-xs text-brand-red font-semibold">{t("dopt.without", { name: ing.name.toLowerCase() })}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-sand px-5 py-3 flex items-center gap-3 bg-white">
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="options-qty-minus" className="rounded-full bg-elevated p-2.5" aria-label={t("dopt.minus")}><Minus className="w-4 h-4" /></button>
            <span className="w-7 text-center font-bold" data-testid="options-qty">{qty}</span>
            <button onClick={() => setQty((q) => q + 1)} data-testid="options-qty-plus" className="rounded-full bg-elevated p-2.5" aria-label={t("dopt.plus")}><Plus className="w-4 h-4" /></button>
          </div>
          <button
            onClick={() => onConfirm({ selected_options: resolved, quantity: qty })}
            data-testid="options-confirm"
            className="flex-1 rounded-full bg-brand-red hover:bg-[#C81F2D] text-white py-3.5 font-semibold text-sm"
          >
            {confirmLabel || t("dopt.confirm")} — {formatGNF(total)}
          </button>
        </div>
      </div>
    </div>
  );
}
